import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRepository, useLogin } from '@city-sage/aurum-avis-labs-components';
import { useIsAuthenticated } from '@city-sage/aurum-avis-labs-components';
import { toast } from 'sonner';
import { QueryKeys } from './QueryKeys';
import { 
  CreateReactionRequestReactableTypeEnum, 
  CreateReactionRequestReactionTypeEnum,
  Reaction,
  RemoveReactionReactableTypeEnum,
  RemoveReactionReactionTypeEnum
} from '@city-sage/core-client';

export interface UseReactionsProps {
  reactableType: 'itinerary' | 'post' | 'stop';
  reactableId: string;
}

export function useReactions({ reactableType, reactableId }: UseReactionsProps) {
  const { api } = useRepository();
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();
  const { promptLoginModal } = useLogin();
  const { data: currentAccount } = useQuery({
    queryKey: [QueryKeys.account.details()],
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated
  });

  // Convert string types to enum types
  const getCreateReactableTypeEnum = (type: string) => {
    switch (type) {
      case 'itinerary':
        return CreateReactionRequestReactableTypeEnum.Itinerary;
      case 'post':
        return CreateReactionRequestReactableTypeEnum.Post;
      case 'stop':
        return CreateReactionRequestReactableTypeEnum.Stop;
      default:
        throw new Error(`Unsupported reactable type: ${type}`);
    }
  };

  const getRemoveReactableTypeEnum = (type: string) => {
    switch (type) {
      case 'itinerary':
        return RemoveReactionReactableTypeEnum.Itinerary;
      case 'post':
        return RemoveReactionReactableTypeEnum.Post;
      case 'stop':
        return RemoveReactionReactableTypeEnum.Stop;
      default:
        throw new Error(`Unsupported reactable type: ${type}`);
    }
  };

  const reactableTypeEnum = getCreateReactableTypeEnum(reactableType);

  // Fetch all reactions for this reactable to get counts and user's reactions
  const { data: allReactions = [], isLoading: isLoadingReactions } = useQuery({
    queryKey: QueryKeys.reaction.byReactable(reactableType.toUpperCase(), reactableId),
    queryFn: () => api.findAllReaction({ 
      filter: JSON.stringify({ 
        reactableType: reactableType.toUpperCase(), 
        reactableId 
      }),
      // TODO this is hardcoded for now, we need to fix this
      limit: 1000
    }),
    enabled: !!reactableId
  });

  const userLike = allReactions.find((r: Reaction) => r.accountId === currentAccount?.id && r.reactionType === 'like' && r.reactableId === reactableId);
  const userDislike = allReactions.find((r: Reaction) => r.accountId === currentAccount?.id && r.reactionType === 'dislike' && r.reactableId === reactableId);
  const isLiked = !!userLike;
  const isDisliked = !!userDislike;

  // Calculate counts
  const likeCount = allReactions.filter(r => r.reactionType === 'like').length;
  const dislikeCount = allReactions.filter(r => r.reactionType === 'dislike').length;

  // Create reaction mutation
  const createReactionMutation = useMutation({
    mutationFn: (reactionType: CreateReactionRequestReactionTypeEnum) => 
      api.createReaction({
        createReactionRequest: {
          reactableType: reactableTypeEnum,
          reactableId,
          reactionType
        }
      }),
    onSuccess: () => {
      // Invalidate and refetch reaction queries
      queryClient.invalidateQueries({ 
        queryKey: QueryKeys.reaction.byReactable(reactableType.toUpperCase(), reactableId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: QueryKeys.reaction.userReactions(reactableType.toUpperCase(), reactableId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: QueryKeys.itinerary.savedStops() 
      });
    },
    onError: (error) => {
      console.error('Failed to create reaction:', error);
      toast.error('Failed to update reaction. Please try again.');
    }
  });

  // Remove reaction mutation
  const removeReactionMutation = useMutation({
    mutationFn: (reactionType: RemoveReactionReactionTypeEnum) => 
      api.removeReaction({
        reactableType: getRemoveReactableTypeEnum(reactableType),
        reactableId,
        reactionType
      }),
    onSuccess: () => {
      // Invalidate and refetch reaction queries
      queryClient.invalidateQueries({ 
        queryKey: QueryKeys.reaction.byReactable(reactableType.toUpperCase(), reactableId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: QueryKeys.reaction.userReactions(reactableType.toUpperCase(), reactableId) 
      });
    },
    onError: (error) => {
      console.error('Failed to remove reaction:', error);
      toast.error('Failed to update reaction. Please try again.');
    }
  });

  // Toggle like function
  const toggleLike = () => {
    if (!isAuthenticated) {
      promptLoginModal({
        title: 'Sign in to like',
        description: 'Create an account or sign in to like content and interact with the community.'
      });
      return;
    }

    if (isLiked) {
      removeReactionMutation.mutate(RemoveReactionReactionTypeEnum.Like);
    } else {
      createReactionMutation.mutate(CreateReactionRequestReactionTypeEnum.Like);
    }
  };

  // Toggle dislike function
  const toggleDislike = () => {
    if (!isAuthenticated) {
      promptLoginModal({
        title: 'Sign in to dislike',
        description: 'Create an account or sign in to react to content and interact with the community.'
      });
      return;
    }

    if (isDisliked) {
      removeReactionMutation.mutate(RemoveReactionReactionTypeEnum.Dislike);
    } else {
      createReactionMutation.mutate(CreateReactionRequestReactionTypeEnum.Dislike);
    }
  };

  return {
    // State
    isLiked,
    isDisliked,
    likeCount,
    dislikeCount,
    isLoading: isLoadingReactions,
    isPending: createReactionMutation.isPending || removeReactionMutation.isPending,
    
    // Actions
    toggleLike,
    toggleDislike,
  };
} 